/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { LessonPlanAPIs } from "../components/LessonPlan/api";
import { groupGrades } from "../utils/groupGrades";
import { exportToExcel } from "../utils/excel";
import { sortClasses } from "../utils/grade";
import { buildQueryString } from "../components/LessonPlan/utils";
import { GlobalContext } from "./GlobalState";

const LessonPlanContext = createContext();

export function LessonPlanProvider({ children }) {
  const { userDetails } = useContext(GlobalContext);
  const [selectedGrades, setSelectedGrades] = useState({});
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [lessonPlans, setLessonPlans] = useState([]);
  const [grades, setGrades] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterDateObject, setFilterDate] = useState({
    fromDate: null,
    toDate: null,
  });
  const [exporting, setExporting] = useState(false);

  // --- Handlers ---
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const formattedDate = value
      ? new Date(value).toISOString().split("T")[0]
      : null;
    setFilterDate((prev) => ({ ...prev, [name]: formattedDate }));
  };

  function selectGrade(event) {
    const gradeId = event.target.value;
    if (!gradeId) {
      setSelectedGrades({});
      return;
    }
    const select = {};
    if (select[gradeId]) {
      delete select[gradeId];
    } else {
      select[gradeId] = [];
    }
    setSelectedGrades({ ...select });
  }

  function selectSubject(event) {
    const [gradeId, subjectId] = (event.target.value || "").split("-");
    if (subjectId && selectedGrades[gradeId].includes(subjectId)) {
      selectedGrades[gradeId] = selectedGrades[gradeId].filter(
        (id) => id !== subjectId
      );
    } else {
      selectedGrades[Object.keys(selectedGrades)[0]] = subjectId
        ? [subjectId]
        : [];
    }
    setSelectedGrades({ ...selectedGrades });
  }

  function clearFilter() {
    setFilterDate({ fromDate: null, toDate: null });
    setSelectedGrades({});
    setSelectedTeacher(null);
  }

  // --- Data fetching ---
  async function fetchLessonPlans() {
    if (loading) return;
    setLoading(true);
    const authToken = localStorage.getItem("authToken");
    try {
      const res = await axios.get(
        `${LessonPlanAPIs.fetchLessonPlans(
          userDetails.userId
        )}?sort=-startDate,_id`,
        {
          headers: { token: authToken },
          validateStatus: () => true,
        }
      );
      if (res.data.status !== "success") throw res.data;
      setLessonPlans(res.data.data.lessonPlans);
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchSections() {
    const authToken = localStorage.getItem("authToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ORIGIN}/api/v1/staffs/${userDetails.userId}`,
        { headers: { token: authToken } }
      );
      if (response.data.status === "success") {
        const sections = response.data.data.staff.sections;
        const grades = groupGrades(sections);
        setGrades(grades);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const filterObjects = useMemo(() => {
    const gradeMap = new Map();
    const teacherMap = new Map();

    lessonPlans.forEach((item) => {
      const { sectionId, subjectId, teacherId } = item;
      if (!sectionId?._id || !subjectId?._id || !teacherId?._id) return;

      const subjectObj = {
        name: subjectId.name,
        value: subjectId._id,
        section: `${sectionId.grade}-${sectionId.section}`,
        sectionId: sectionId._id,
      };

      if (!gradeMap.has(sectionId._id)) {
        gradeMap.set(sectionId._id, {
          name: `${sectionId.grade}-${sectionId.section}`,
          value: sectionId._id,
          subjects: [subjectObj],
        });
      } else {
        const grade = gradeMap.get(sectionId._id);
        if (!grade.subjects.find((sub) => sub.value === subjectId._id)) {
          grade.subjects.push(subjectObj);
        }
      }

      if (!teacherMap.has(teacherId._id) && userDetails?.role !== "teacher") {
        teacherMap.set(teacherId._id, {
          name: teacherId.name,
          value: teacherId._id,
        });
      }
    });

    return {
      grades: sortClasses(Array.from(gradeMap.values())),
      teachers: Array.from(teacherMap.values()).sort((a,b) => a.name.localeCompare(b.name))
    };
  }, [lessonPlans]);

  const filterSubjects = useMemo(() => {
    return filterObjects.grades
      .filter((grade) => Object.keys(selectedGrades).includes(grade.value))
      .map((grade) => grade.subjects)
      .flat(1);
  }, [selectedGrades, filterObjects]);

  const filteredPlans = useMemo(() => {
    const from =filterDateObject.fromDate
        ? new Date(`${filterDateObject.fromDate}T00:00:00`)
        : null;
      const to =filterDateObject.toDate
        ? new Date(`${filterDateObject.toDate}T23:59:59`)
        : null;
    return lessonPlans.filter((lp) => {
      
      if (selectedTeacher && lp?.teacherId?._id !== selectedTeacher)
        return false;

      if (filterDateObject.fromDate || filterDateObject.toDate) {
        const createdAt = new Date(lp.startDate);
        if (from && to && (createdAt < from || createdAt > to)) return false;
        if (from && createdAt < from) return false;
        if (to && createdAt > to) return false;
      }

      if (!Object.keys(selectedGrades).length) return true;
      if (selectedGrades[lp.sectionId._id]) {
        if (!selectedGrades[lp.sectionId._id].length) return true;
        if (selectedGrades[lp.sectionId._id].includes(lp.subjectId._id))
          return true;
        return false;
      }
      return false;
    });
  }, [selectedGrades, selectedTeacher, lessonPlans, filterDateObject]);

  const groupedPlans = useMemo(() => {
    // Map: teacherId -> date -> lessonPlans[]
    const teacherDayMap = new Map();

    filteredPlans.forEach((lp) => {
      const teacherId = lp.teacherId?._id;
      if (!teacherId) return;

      const dateKey = new Date(lp.startDate).toISOString().split("T")[0]; // YYYY-MM-DD

      if (!teacherDayMap.has(teacherId)) {
        teacherDayMap.set(teacherId, new Map());
      }
      const dayMap = teacherDayMap.get(teacherId);

      if (!dayMap.has(dateKey)) {
        dayMap.set(dateKey, []);
      }
      dayMap.get(dateKey).push(lp);
    });

    // Convert map to desired structure
    const result = [];

    teacherDayMap.forEach((dayMap) => {
      dayMap.forEach((plans) => {
        // Sort by period
        const sortedPlans = plans.sort((a, b) => {
          const pa = Number(a.time) || 0;
          const pb = Number(b.time) || 0;
          return pa - pb;
        });

        // Ensure each plan has period field (default 0 if missing)
        const normalizedPlans = sortedPlans.map((p) => ({
          ...p,
          time: Number(p.time) || 0,
        }));

        result.push(normalizedPlans);
      });
    });

    return result.sort((a,b)=> new Date(b[0].startDate).getTime() - new Date(a[0].startDate).getTime());
  }, [filteredPlans, lessonPlans]);

  async function exportLessonPlans() {
    if (exporting) return;
    setExporting(true);
    try {
      const queryString = buildQueryString({
        teacherId: selectedTeacher,
        selectedGrades,
        dateFilter: filterDateObject,
      });
      const res = await axios.get(
        LessonPlanAPIs.fetchGroupedPlans(userDetails.userId, queryString),
        {
          headers: {
            token: localStorage.getItem("authToken"),
          },
          validateStatus: () => true,
        }
      );

      const plans = res.data?.data?.plans || [];
      const data = plans
        .filter((plan) => plan?.teacherId)
        .sort((a, b) => {
          return new Date(b.startDate) - new Date(a.startDate);
        })
        .map((lp) => {
          const base = {
            Teacher: lp.teacherId?.name || "N/A",
            Period: lp.time || "Flexible",
            Grade: `${lp.sectionId.grade}-${lp.sectionId.section}`,
            Subject: lp.subjectId.name,
            Chapter: lp.chapterId?.name || lp?.customChapter || "N/A",
            Topic: lp?.topicId?.name || lp?.customTopic || "N/A",
            "Sub Topic": lp.customModule || "N/A",
          };

          // Sort cols by colIndex, then reduce to object
          const cols = (lp.cols || [])
            .sort((a, b) => a.colIndex - b.colIndex)
            .reduce((acc, col) => {
              if (["link", "text"].includes(col.colType)) {
                acc[col.colName] = col.text || "N/A";
              } else {
                acc[col.colName] = col.resources.length
                  ? col.resources
                      .map((resource) => {
                        return `${resource?.grade}>${resource?.subject}>${
                          resource?.chapter
                        }>${resource?.topic}>${resource?.id?.name || ""}\n`;
                      })
                      .join("")
                  : "N/A";
              }
              return acc;
            }, {});

          // Merge base + cols
          return {
            ...base,
            ...cols,
            Date: new Date(lp.startDate).toLocaleDateString(),
          };
        });
      exportToExcel(data, `Lesson Plans ${new Date().toLocaleDateString()}`);
    } catch (err) {
      alert(err.message || "Something went wrong!");
    }
    setExporting(false);
  }

  // --- Initial Fetch ---
  useEffect(() => {
    if (!userDetails?.userId || lessonPlans.length) return;
    fetchSections();
    fetchLessonPlans();
  }, [userDetails]);

  return (
    <LessonPlanContext.Provider
      value={{
        loading,
        lessonPlans,
        setLessonPlans,
        grades,
        filterObjects,
        filterSubjects,
        filteredPlans,
        groupedPlans,
        selectedGrades,
        selectedTeacher,
        filterDateObject,
        setSelectedTeacher,
        selectGrade,
        selectSubject,
        handleDateChange,
        clearFilter,
        exportLessonPlans,
        exporting,
      }}
    >
      {children}
    </LessonPlanContext.Provider>
  );
}

export function useLessonPlans() {
  return useContext(LessonPlanContext);
}
